import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Avatar,
  Box,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import strings from '../../../../common/Translation/Translate';
import Config from '../../../../utils/Config';
import axiosInstance from '../../../../utils/axios';
import { getAvatarPalette } from '../../../../utils/avatarColors';
import { PageProps } from '../../../../utils/constants/data';
import { privileges } from '../../../../utils/constants/privileges';
import { routes } from '../../../../utils/constants/routes';
import { URLS } from '../../../../utils/constants/urls';
import { filterDataforApi, pageNo, rowCount } from 'utils/redux/reducer/filter-slice';
import { createDropdownData } from '../../../../utils/validators/HelperFunctions';
import ListPage from '../../CommonPage';
import { lastUpdatedTableColumn } from '../../CommonPage/lastUpdatedColumn';
import { VenueConfigsExpandPanel } from './VenueConfigsExpandPanel';

type VenueRow = {
  id: number;
  venueName?: string;
  imageUrl?: string;
  venueTypeName?: string;
  available?: boolean;
  configurationCount?: number;
  sortOrder?: number;
  editedAt?: string;
  editedBy?: string;
};

type DropdownOption = { label: string; value: number | string };

const statusChipSx = {
  height: 24,
  borderRadius: '999px',
  fontWeight: 600,
  fontSize: 12,
  '& .MuiChip-label': { px: 1.25 },
};

const resolveVenueImageUrl = (imageUrl?: string) => {
  if (!imageUrl) return undefined;
  if (/^https?:\/\//i.test(imageUrl)) return imageUrl;
  return `${Config.BaseURL}${imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`}`;
};

const getInitials = (name?: string) => {
  if (!name?.trim()) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

const withVenueTypeFilter = (filter: unknown, venueTypeId: string) => {
  const base = Array.isArray(filter)
    ? filter.filter((item: unknown) => typeof item !== 'string' || !item.startsWith('venueTypeId:'))
    : [];

  const cleaned: string[] = [];
  base.forEach((item: string) => {
    if (item === 'and') {
      if (cleaned.length === 0 || cleaned[cleaned.length - 1] === 'and') return;
      cleaned.push(item);
      return;
    }
    cleaned.push(item);
  });
  if (cleaned.length && cleaned[cleaned.length - 1] === 'and') cleaned.pop();

  if (!venueTypeId) return cleaned;
  if (cleaned.length) cleaned.push('and');
  cleaned.push(`venueTypeId:eq:${venueTypeId}`);
  return cleaned;
};

const VenueListAvatar: FC<{ name?: string; imageUrl?: string }> = ({ name, imageUrl }) => {
  const [imageFailed, setImageFailed] = useState(false);
  const resolvedUrl = resolveVenueImageUrl(imageUrl);
  const showImage = Boolean(resolvedUrl) && !imageFailed;
  const palette = getAvatarPalette(name);

  useEffect(() => {
    setImageFailed(false);
  }, [imageUrl]);

  return (
    <Avatar
      src={showImage ? resolvedUrl : undefined}
      variant="rounded"
      alt={name || 'Venue'}
      imgProps={{ onError: () => setImageFailed(true) }}
      sx={{
        width: 56,
        height: 42,
        borderRadius: 1.25,
        fontSize: 12,
        fontWeight: 600,
        flexShrink: 0,
        bgcolor: showImage ? 'transparent' : palette.bg,
        color: palette.color,
        boxShadow: 'none',
        backgroundImage: 'none',
      }}>
      {getInitials(name)}
    </Avatar>
  );
};

const VenueList: FC = () => {
  const index = useSelector(pageNo);
  const filter = useSelector(filterDataforApi);
  const pageSize = useSelector(rowCount);
  const [searchParams, setSearchParams] = useSearchParams();
  const [pageResponse, setPageResponse] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [expandedVenueId, setExpandedVenueId] = useState<number | null>(null);
  const [venueTypes, setVenueTypes] = useState<DropdownOption[]>([]);
  const [selectedVenueTypeId, setSelectedVenueTypeId] = useState(() => searchParams.get('venueTypeId') || '');

  useEffect(() => {
    const venueTypeIdFromUrl = searchParams.get('venueTypeId') || '';
    setSelectedVenueTypeId(prev => (prev === venueTypeIdFromUrl ? prev : venueTypeIdFromUrl));
  }, [searchParams]);

  useEffect(() => {
    const fetchVenueTypes = async () => {
      try {
        const { data } = await axiosInstance.get(URLS.venueTypes);
        setVenueTypes(createDropdownData(data, ['id', 'name']) || []);
      } catch (error) {
        console.error(error);
      }
    };
    void fetchVenueTypes();
  }, []);

  const handleVenueTypeFilterChange = (value: string) => {
    setSelectedVenueTypeId(value);
    const nextParams = new URLSearchParams(searchParams);
    if (value) nextParams.set('venueTypeId', value);
    else nextParams.delete('venueTypeId');
    setSearchParams(nextParams, { replace: true });
  };

  const handleToggleExpand = (row: VenueRow) => {
    setExpandedVenueId(prev => (prev === row.id ? null : row.id));
  };

  const pageConfig = useMemo(
    () => ({
      title: 'Venues',
      entityLabel: 'Venues',
      subtitle: 'Manage venues. Click a row to edit, or expand configurations via the arrow / Configurations column.',
      searchField: 'search',
      searchPlaceholder: 'Search venues',
      AddButtonText: strings.venue_button_new,
      AddButtonUrl: routes.addVenue,
      editButtonUrl: routes.editVenue,
      listApi: URLS.venue,
      deleteApi: URLS.get_edit_delete_VenueById,
      addPrivilege: privileges.add_venue,
      editPrivilege: privileges.edit_venue,
      deletePrivilege: privileges.delete_venue,
      isRowExpanded: (row: VenueRow) => expandedVenueId === row.id,
      renderExpandedRow: (row: VenueRow) => (
        <VenueConfigsExpandPanel venueId={row.id} venueName={row.venueName} />
      ),
      headerFilters: () => (
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel sx={{ fontSize: 12 }}>Venue type</InputLabel>
          <Select
            label="Venue type"
            value={selectedVenueTypeId}
            onChange={event => handleVenueTypeFilterChange(String(event.target.value))}
            sx={{
              height: 36,
              fontSize: 13,
              bgcolor: '#fff',
              '& .MuiSelect-select': { py: 0.75 },
            }}>
            <MenuItem value="">All venue types</MenuItem>
            {venueTypes.map(option => (
              <MenuItem key={option.value} value={String(option.value)} sx={{ fontSize: 13 }}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ),
      filterChips: selectedVenueTypeId
        ? [
            {
              key: 'venueTypeId',
              label: `Venue type : ${
                venueTypes.find(option => String(option.value) === selectedVenueTypeId)?.label || selectedVenueTypeId
              }`,
            },
          ]
        : [],
      onClearFilterChip: (key: string) => {
        if (key === 'venueTypeId') handleVenueTypeFilterChange('');
      },
      onClearAllFilterChips: () => handleVenueTypeFilterChange(''),
      tableColumn: [
        {
          field: 'venueName',
          headerName: 'Venue Name',
          flex: 2.4,
          sortable: true,
          localField: 'localizedAttributes.name',
          renderCell: (_value: any, row: VenueRow) => {
            const isExpanded = expandedVenueId === row.id;
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
                <IconButton
                  size="small"
                  onClick={e => {
                    e.stopPropagation();
                    handleToggleExpand(row);
                  }}
                  sx={{ p: 0.25, color: '#667085' }}>
                  {isExpanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                </IconButton>
                <VenueListAvatar name={row.venueName} imageUrl={row.imageUrl} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#101828', lineHeight: 1.3 }}>
                    {row.venueName || '—'}
                  </Typography>
                  {row.venueTypeName ? (
                    <Chip
                      size="small"
                      label={row.venueTypeName}
                      sx={{
                        mt: 0.35,
                        height: 20,
                        fontSize: 11,
                        fontWeight: 600,
                        bgcolor: '#F4F3FF',
                        color: '#5925DC',
                      }}
                    />
                  ) : null}
                </Box>
              </Box>
            );
          },
        },
        {
          field: 'available',
          headerName: 'Status',
          flex: 0.9,
          sortable: true,
          renderCell: (_value: any, row: VenueRow) => (
            <Chip
              size="small"
              label={
                row.available
                  ? strings.venue_column_available || 'Available'
                  : strings.venue_column_not_available || 'Not Available'
              }
              sx={{
                ...statusChipSx,
                bgcolor: row.available ? '#ECFDF3' : '#F2F4F7',
                color: row.available ? '#027A48' : '#344054',
              }}
            />
          ),
        },
        {
          field: 'sortOrder',
          headerName: 'Sort Order',
          flex: 0.8,
          sortable: true,
          renderCell: (value: any) => (
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#344054' }}>{value ?? '—'}</Typography>
          ),
        },
        {
          field: 'configurationCount',
          headerName: 'Configurations',
          flex: 1.1,
          sortable: false,
          renderCell: (_value: any, row: VenueRow) => {
            const count = Number(row.configurationCount ?? 0);
            return (
              <Typography
                component="span"
                role="button"
                onClick={e => {
                  e.stopPropagation();
                  handleToggleExpand(row);
                }}
                sx={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: count > 0 ? '#175CD3' : '#98A2B3',
                  textDecoration: count > 0 ? 'underline' : 'none',
                  textUnderlineOffset: '2px',
                  cursor: 'pointer',
                }}>
                {count === 0 ? 'Add configuration' : count === 1 ? '1 configuration' : `${count} configurations`}
              </Typography>
            );
          },
        },
        lastUpdatedTableColumn(),
      ],
    }),
    [expandedVenueId, selectedVenueTypeId, venueTypes],
  );

  const getList = useCallback(
    async (filterData: PageProps) => {
      try {
        setLoading(true);
        const body = filterData
          ? { ...filterData, filter: withVenueTypeFilter(filterData.filter, selectedVenueTypeId) }
          : {
              page: index + 1,
              size: pageSize,
              filter: withVenueTypeFilter(filter, selectedVenueTypeId),
              sort: ['sortOrder:asc'],
            };
        const { data } = await axiosInstance.post(pageConfig.listApi, body);
        setPageResponse(data);
      } catch (error: any) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
    [pageConfig.listApi, index, pageSize, filter, selectedVenueTypeId],
  );

  useEffect(() => {
    (async () => {
      await getList(null as any);
    })();
  }, [getList]);

  return <ListPage isLoading={loading} pageConfig={pageConfig} pageResponse={pageResponse} getList={getList} />;
};

export default VenueList;
